import { lazy, Suspense, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Circle, Eye, Plus, Redo2, Square, StickyNote, Undo2, X } from "lucide-react";
import { usePatientBoard } from "../../application/useConstellation";
import { ZONE_COLORS } from "../zoneColors";
import {
    BoardAction,
    createNote,
    guardedBoardReducer,
    type EditorIdentity,
} from "../../application/boardReducer";
import { useBoardLinks } from "../../application/useBoardLinks";
import {
    canRedo,
    canUndo,
    historyReducer,
    initHistory,
} from "../../application/historyReducer";
import { emptyBoardState, FaceExpression, FigureAction, FigurePreset, Puppet } from "../../domain/types";
import { figureSpec } from "../../domain/figures";
import { usePublicTherapistFigures } from "../../application/useTherapistFigures";
import { joinConstellationBoard } from "../../application/constellationSessions";
import ConstellationWorkspace from "../components/ConstellationWorkspace";
import PuppetBuilder from "../components/PuppetBuilder";
import PuppetList from "../components/PuppetList";
import FacePicker from "../components/FacePicker";
import DraggablePanel from "../components/DraggablePanel";
import LanguageSelector from "../../../../shared/ui/header/LanguageSelector";
import PatientJoinScreen from "../components/PatientJoinScreen";
import LinkEditorModal from "../components/LinkEditorModal";
import KickedScreen from "../components/KickedScreen";
import ImmersiveToolbar from "../components/ImmersiveToolbar";
import BoardOverlay from "../components/BoardOverlay";
import { useBoardView } from "../../application/useBoardView";
import { FigureOwnerType } from "../../domain/types";

// Hidden off-screen canvas that bakes 3D figure thumbnails for the picker.
const FigureThumbnailBaker = lazy(() => import("../components/FigureThumbnailBaker"));

/**
 * Posted to the call around a framed board when it is finished — kept in step
 * with `TOOL_BOARD_CLOSED_MESSAGE` in the session module, which listens.
 */
const MEETING_BOARD_CLOSED = "alchely:meeting-board-closed";

/**
 * Public patient page reached by a one-time token link. Rendered OUTSIDE the
 * authenticated app shell — no login required. Minimal chrome.
 */
export default function PatientConstellationPage() {
    const { token } = useParams<{ token: string }>();
    // Set when the board is framed inside a call: the name the patient joined
    // the call under, so they are not asked for it a second time.
    const [searchParams] = useSearchParams();
    const callName = searchParams.get("name")?.trim() ?? "";
    const { t } = useTranslation();
    const { data, status, saveStatus, tokenError, save, sessionType, participantId, setParticipantId, ownershipError, clearOwnershipError } = usePatientBoard(token);
    const [joinError, setJoinError] = useState<string | null>(null);
    const [joining, setJoining] = useState(false);
    // An individual board only asks for a name to label figures with, so a
    // name from the call is all it needs.
    const [displayName, setDisplayName] = useState<string>(callName);
    const [history, hdispatch] = useReducer(historyReducer, emptyBoardState(), initHistory);
    const state = history.present;
    // The patient is a participant, not the therapist: they may only edit the
    // figures they own. The reducer enforces this locally; the server is the
    // authority (a rejected write triggers a resync in the hook).
    //
    // Individual boards have no join and no participant record, so we give the
    // single patient a stable client-side owner id ("self"). It is stamped onto
    // their figures (so they carry the patient's name and stay editable by the
    // patient) but is NOT sent as the wire participant_id — the individual
    // server path is unauthenticated single-writer and skips enforcement.
    const editorParticipantId =
        sessionType === "individual" ? "self" : (participantId ?? null);
    const guardedReduce = useMemo(() => {
        const identity: EditorIdentity = {
            isTherapist: false,
            participantId: editorParticipantId,
            displayName,
        };
        return guardedBoardReducer(identity);
    }, [editorParticipantId, displayName]);
    const dispatch = useCallback(
        (action: BoardAction) => hdispatch({ type: "do", action, reduce: guardedReduce }),
        [guardedReduce],
    );
    const undo = useCallback(() => hdispatch({ type: "undo" }), []);
    const redo = useCallback(() => hdispatch({ type: "redo" }), []);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
    const [editingZoneId, setEditingZoneId] = useState<string | null>(null);
    // Mobile: controls live in a bottom sheet so the board is the hero.
    const [toolsOpen, setToolsOpen] = useState(false);
    // Board view controls (owner labels, fit-to-board, immersive fullscreen).
    // The patient board starts directly in immersive mode (issue #197).
    const view = useBoardView();
    // Immersive-mode UI: the figure list, the add-figure picker and the
    // add-area (shape + name) modal.
    const [listOpen, setListOpen] = useState(true);
    const [figurePickerOpen, setFigurePickerOpen] = useState(false);
    // Bake the picker's 3D thumbnails only once somebody opens it. Baking runs
    // a second WebGL context and reads every figure back off the GPU; on a
    // fresh browser (a patient joining from a call) that landed on the first
    // seconds of the board, for a picker that shows emoji until it is done.
    const [thumbnailsWanted, setThumbnailsWanted] = useState(false);
    if (figurePickerOpen && !thumbnailsWanted) setThumbnailsWanted(true);
    // The owning therapist's reusable figures (read-only; patient can place them).
    const { data: therapistFigures = [] } = usePublicTherapistFigures(token);

    // Start directly in immersive fullscreen once the board is ready.
    useEffect(() => {
        if (status === "ready") view.enterImmersive();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    // The figure list only shows the participant's own figures (issue #197).
    const ownFigures = useMemo(
        () =>
            state.puppets.filter(
                (p) =>
                    (p.ownerType ?? FigureOwnerType.THERAPIST) === FigureOwnerType.PARTICIPANT &&
                    p.ownerId === editorParticipantId,
            ),
        [state.puppets, editorParticipantId],
    );

    // Skip the autosave that the next hydration triggers, so re-applying a
    // remote board does not echo it straight back as a new version (ping-pong).
    const skipSaveRef = useRef(false);

    // Hydrate local state when the board loads or a newer remote version polls
    // in (resets history).
    useEffect(() => {
        if (data) {
            skipSaveRef.current = true;
            hdispatch({ type: "reset", state: data.state });
        }
    }, [data]);

    // Autosave on every local mutation (debounced inside the hook).
    useEffect(() => {
        if (skipSaveRef.current) {
            skipSaveRef.current = false;
            return;
        }
        if (status === "ready") save(state);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    // Keyboard: Cmd/Ctrl+Z = undo, Cmd/Ctrl+Shift+Z (or Ctrl+Y) = redo.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!(e.metaKey || e.ctrlKey)) return;
            const key = e.key.toLowerCase();
            if (key === "z") {
                e.preventDefault();
                if (e.shiftKey) redo();
                else undo();
            } else if (key === "y") {
                e.preventDefault();
                redo();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [undo, redo]);

    // Links between figures (shared state machine with the therapist board).
    const links = useBoardLinks({
        state,
        dispatch,
        selectedId,
        setSelectedId,
        onEnterLinkMode: () => setEditingId(null),
    });
    const editingLink = links.editingLink;

    const handleJoin = async (name: string) => {
        if (!token) return;
        setJoining(true);
        setJoinError(null);
        try {
            const joinData = await joinConstellationBoard(token, name);
            setDisplayName(name);
            setParticipantId(joinData.participant_id);
        } catch (err: unknown) {
            const errStatus = (err as { response?: { status?: number } })?.response?.status;
            if (errStatus === 400) {
                setJoinError(t("constellation.join.nameTooLong"));
            } else if (errStatus === 403) {
                setJoinError(t("constellation.join.notAllowed"));
            } else {
                setJoinError(t("constellation.join.error"));
            }
        } finally {
            setJoining(false);
        }
    };

    // Framed inside a call: once the therapist has finished the board, hand
    // the screen back to the call — out of fullscreen first, or the patient
    // is left on an expanded "session finished" page instead of the faces.
    useEffect(() => {
        // An expired link, in a call, means the same: the board is over.
        if ((tokenError !== "finalized" && tokenError !== "expired") || window.parent === window) return;
        // Told only once out: the call takes the frame away when it hears,
        // and a frame removed mid-transition can freeze the screen.
        const leaving =
            document.fullscreenElement && document.exitFullscreen
                ? document.exitFullscreen().catch(() => undefined)
                : Promise.resolve();
        void leaving.then(() =>
            window.parent.postMessage({ type: MEETING_BOARD_CLOSED }, window.location.origin)
        );
    }, [tokenError]);

    // A shared board joins under the call's name instead of asking for one.
    // Once only: a refused join falls through to the screen that asks, with
    // the reason on it.
    const autoJoinedRef = useRef(false);
    useEffect(() => {
        if (!callName || autoJoinedRef.current) return;
        if (sessionType === null || sessionType === "individual" || participantId !== null) return;
        autoJoinedRef.current = true;
        void handleJoin(callName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callName, sessionType, participantId]);

    if (tokenError === "removed") {
        return <KickedScreen />;
    }

    if (sessionType !== null && sessionType !== "individual" && participantId === null) {
        return (
            <PatientJoinScreen
                onJoin={handleJoin}
                loading={joining}
                error={joinError}
            />
        );
    }

    // Individual boards have no join, but we still ask the patient for a name so
    // their figures are labelled with it (issue #197). Local only — no API call.
    if (sessionType === "individual" && !displayName) {
        return (
            <PatientJoinScreen
                onJoin={(name) => setDisplayName(name)}
                loading={false}
                error={null}
            />
        );
    }

    if (tokenError === "finalized") {
        return (
            <div className="flex h-screen flex-col items-center justify-center gap-3 p-6 text-center">
                <span className="text-5xl" aria-hidden>
                    ✨
                </span>
                <h1 className="text-xl font-semibold text-gray-800">
                    {t("constellation.patient.sessionFinalizedTitle")}
                </h1>
                <p className="max-w-sm text-sm text-gray-500">
                    {t("constellation.patient.sessionFinalizedBody")}
                </p>
            </div>
        );
    }

    if (tokenError === "expired") {
        return (
            <div role="alert" className="flex h-screen items-center justify-center p-6 text-center">
                {t("constellation.patient.linkExpired")}
            </div>
        );
    }

    if (status === "error") {
        return (
            <div role="alert" className="flex h-screen items-center justify-center p-6 text-center">
                {t("constellation.patient.loadError")}
            </div>
        );
    }

    const addPuppet = (puppet: Puppet) => dispatch({ type: "addPuppet", puppet });
    const movePuppet = (id: string, x: number, z: number) =>
        dispatch({ type: "movePuppet", id, position: { x, y: 0, z } });
    const rotatePuppet = (id: string, delta: number) => {
        const current = state.puppets.find((p) => p.id === id);
        if (current) dispatch({ type: "rotatePuppet", id, rotationY: current.rotationY + delta });
    };
    const renamePuppet = (id: string, name: string) =>
        dispatch({ type: "renamePuppet", id, name });
    const removePuppet = (id: string) => {
        dispatch({ type: "removePuppet", id });
        if (selectedId === id) setSelectedId(null);
        if (editingId === id) setEditingId(null);
    };
    const setPuppetFace = (id: string, face: FaceExpression) =>
        dispatch({ type: "updatePuppetAttributes", id, face });
    const setPuppetNote = (id: string, note: string) =>
        dispatch({ type: "setNote", id, note });
    const setPuppetAction = (id: string, action: FigureAction) =>
        dispatch({ type: "setAction", id, action });
    const setTitle = (title: string) => dispatch({ type: "setTitle", title });
    const openEdit = (id: string) => {
        setSelectedId(id);
        setEditingId(id);
    };

    // --- Free-floating notes ---
    const addNote = () => {
        const note = createNote("", { x: 0, y: 0, z: 0 });
        dispatch({ type: "addNote", note });
        setSelectedId(note.id);
        setEditingNoteId(note.id);
    };
    const moveNote = (id: string, x: number, z: number) =>
        dispatch({ type: "moveNote", id, position: { x, y: 0, z } });
    const removeNote = (id: string) => {
        dispatch({ type: "removeNote", id });
        if (selectedId === id) setSelectedId(null);
        if (editingNoteId === id) setEditingNoteId(null);
    };
    const openNoteEdit = (id: string) => {
        setSelectedId(id);
        setEditingNoteId(id);
    };
    const editingNote = (state.notes ?? []).find((n) => n.id === editingNoteId) ?? null;

    // --- Floor delimitation areas (zones): therapist-only. The patient may
    // view them but not add or edit them (issue #197). ---
    const moveZone = (id: string, x: number, z: number) =>
        dispatch({ type: "moveZone", id, position: { x, y: 0, z } });
    const removeZone = (id: string) => {
        dispatch({ type: "removeZone", id });
        if (selectedId === id) setSelectedId(null);
        if (editingZoneId === id) setEditingZoneId(null);
    };
    const openZoneEdit = (id: string) => {
        setSelectedId(id);
        setEditingZoneId(id);
    };
    const resizeZone = (id: string, dims: { radius?: number; width?: number; depth?: number }) =>
        dispatch({ type: "editZone", id, ...dims });
    const resizeZoneStep = (
        zone: { id: string; shape: string; radius: number; width: number; depth: number },
        dir: number,
    ) => {
        const rMin = data?.min_zone_radius ?? 0.2;
        const rMax = data?.max_zone_radius ?? 6;
        if (zone.shape === "square") {
            resizeZone(zone.id, {
                width: Math.max(2 * rMin, Math.min(2 * rMax, zone.width + dir * 0.6)),
                depth: Math.max(2 * rMin, Math.min(2 * rMax, zone.depth + dir * 0.6)),
            });
        } else {
            resizeZone(zone.id, {
                radius: Math.max(rMin, Math.min(rMax, zone.radius + dir * 0.3)),
            });
        }
    };
    const editingZone = (state.zones ?? []).find((z) => z.id === editingZoneId) ?? null;
    // Board styling (shape/color/panorama) is therapist-only (issue #197).

    const editingPuppet = state.puppets.find((p) => p.id === editingId) ?? null;
    // Actions + emotions apply to humans and to custom avatars.
    const editingIsHuman =
        editingPuppet !== null &&
        (figureSpec(editingPuppet.figure).category === "human" ||
            editingPuppet.figure === FigurePreset.AVATAR);

    // Shared control panel — used in the desktop aside and the mobile bottom sheet.
    const controls = (
        <div className="flex flex-col">
            <div className="p-4">
                <h1 className="text-lg font-semibold">{t("constellation.patient.title")}</h1>
                <p className="text-xs text-gray-500">{t("constellation.patient.subtitle")}</p>
                <input
                    type="text"
                    value={state.title ?? ""}
                    maxLength={data?.max_name_length}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("constellation.patient.namePlaceholder")}
                    aria-label={t("constellation.patient.sessionName")}
                    className="w-full rounded-md border px-2 py-1 text-sm"
                />
                <div className="mt-3 flex gap-2">
                    <button
                        type="button"
                        onClick={undo}
                        disabled={!canUndo(history)}
                        aria-label={t("constellation.history.undo")}
                        title={t("constellation.history.undo")}
                        className="flex min-h-[36px] flex-1 items-center justify-center gap-1 rounded-md border px-2 text-sm disabled:opacity-40"
                    >
                        <Undo2 className="h-4 w-4" /> {t("constellation.history.undo")}
                    </button>
                    <button
                        type="button"
                        onClick={redo}
                        disabled={!canRedo(history)}
                        aria-label={t("constellation.history.redo")}
                        title={t("constellation.history.redo")}
                        className="flex min-h-[36px] flex-1 items-center justify-center gap-1 rounded-md border px-2 text-sm disabled:opacity-40"
                    >
                        <Redo2 className="h-4 w-4" /> {t("constellation.history.redo")}
                    </button>
                </div>
            </div>

            {/* Primary action: add a figure. */}
            <PuppetBuilder
                onAdd={addPuppet}
                disabled={status !== "ready"}
                maxNameLength={data?.max_name_length}
                therapistFigures={therapistFigures}
            />

            <div className="flex flex-col gap-2 px-4 pb-4">
                <button
                    type="button"
                    onClick={addNote}
                    disabled={status !== "ready"}
                    className="flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-2 text-sm font-medium text-yellow-900 transition-colors hover:bg-yellow-100 disabled:opacity-50"
                >
                    <StickyNote className="h-4 w-4" />
                    {t("constellation.note.add")}
                </button>
                {/* Areas, board styling and the connected-people list are
                    therapist-only (issue #197). */}
            </div>
        </div>
    );

    return (
        <div className="flex h-screen flex-col">
            {thumbnailsWanted && (
                <Suspense fallback={null}>
                    <FigureThumbnailBaker />
                </Suspense>
            )}
            <header className="flex h-14 shrink-0 items-center justify-between border-b bg-white px-4">
                <img src="/images/logo/logo.png" alt="Alchely" className="h-8" />
                <div className="flex items-center gap-3">
                    {status === "ready" && (
                        <span className="flex items-center gap-1.5 rounded-full bg-gray-50 px-3 py-1 text-xs text-gray-600">
                            <Eye className="h-3.5 w-3.5 text-green-500" />
                            <span className="hidden sm:inline">
                                {t("constellation.patient.therapistViewing")}
                            </span>
                            <span className="text-gray-300">·</span>
                            {saveStatus === "saving" ? (
                                <span className="text-content-tertiary">{t("constellation.therapist.sync.saving")}</span>
                            ) : saveStatus === "error" ? (
                                <span className="text-amber-600">{t("constellation.patient.reconnecting")}</span>
                            ) : (
                                <span className="text-green-600">{t("constellation.therapist.sync.saved")}</span>
                            )}
                        </span>
                    )}
                    <LanguageSelector />
                </div>
            </header>
            <div className="flex flex-1 flex-col overflow-hidden md:flex-row">
            <aside className="hidden w-80 shrink-0 flex-col overflow-y-auto border-r bg-white md:flex lg:w-96">
                {controls}
            </aside>

            <main className="relative min-w-0 flex-1">
              <div
                ref={view.containerRef}
                className={
                    view.immersive
                        ? "fixed inset-0 z-[60] bg-slate-900"
                        : "relative h-full w-full"
                }
              >
                {/* Alchely brand mark, top-left of the board. White while
                    immersive, where the ground is slate-900 and the navy
                    wordmark would not be there at all. */}
                <img
                    src="/images/logo/logo.png"
                    alt="Alchely"
                    className={`pointer-events-none absolute left-4 top-4 z-[64] h-8 opacity-90 ${
                        view.immersive ? "brightness-0 invert" : ""
                    }`}
                />
                {links.linkingFromId && (
                    <div className="absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-sm font-medium text-white shadow">
                        {t("constellation.link.pickTarget")}
                        <button
                            type="button"
                            onClick={links.cancelLinking}
                            className="rounded-full bg-white/20 px-2 py-0.5 text-xs hover:bg-white/30"
                        >
                            {t("common.cancel", "Cancel")}
                        </button>
                    </div>
                )}
                {state.title ? (
                    <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-full bg-white/90 px-4 py-1.5 text-sm font-semibold text-gray-800 shadow-theme-sm">
                        {state.title}
                    </div>
                ) : null}

                {/* Onboarding hint — shown on an empty board. */}
                {status === "ready" && state.puppets.length === 0 && (
                    <div className="pointer-events-none absolute inset-x-0 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-2 px-6 text-center">
                        <span className="text-4xl" aria-hidden>
                            👋
                        </span>
                        <p className="max-w-xs text-sm font-medium text-gray-600">
                            {t("constellation.patient.emptyHint")}
                        </p>
                    </div>
                )}
                {ownershipError && (
                    <div
                        role="status"
                        className="absolute left-1/2 top-4 z-30 -translate-x-1/2 rounded-md bg-amber-100 px-4 py-2 text-sm text-amber-900 shadow"
                        onAnimationEnd={clearOwnershipError}
                    >
                        {t("constellation.owners.notAllowed")}
                        <button
                            type="button"
                            className="ml-3 font-semibold underline"
                            onClick={clearOwnershipError}
                        >
                            {t("common.dismiss", "Dismiss")}
                        </button>
                    </div>
                )}
                <ConstellationWorkspace
                    state={state}
                    loading={status === "loading"}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    onMove={movePuppet}
                    onRotate={rotatePuppet}
                    onRemove={removePuppet}
                    onEdit={openEdit}
                    onNoteMove={moveNote}
                    onNoteEdit={openNoteEdit}
                    onNoteRemove={removeNote}
                    onZoneMove={moveZone}
                    onZoneEdit={openZoneEdit}
                    onZoneRemove={removeZone}
                    onZoneResize={resizeZone}
                    onStartLink={links.startLink}
                    linkingFromId={links.linkingFromId}
                    onLinkTarget={links.linkTarget}
                    onLinkEdit={links.openLinkEdit}
                    onLinkRemove={links.removeLink}
                    viewer={{ isTherapist: false, participantId: editorParticipantId }}
                    therapistLabel={t("constellation.owners.therapist")}
                    showOwners
                    fitRequest={view.fitRequest}
                    zonesReadOnly
                />

                {/* Immersive action bar (issue #197). */}
                {view.immersive && (
                    <ImmersiveToolbar
                        onAddFigure={() => setFigurePickerOpen(true)}
                        onAddNote={addNote}
                        onFit={view.requestFit}
                        onToggleNativeFullscreen={view.toggleNativeFullscreen}
                        nativeFullscreen={view.nativeFullscreen}
                        onToggleList={() => setListOpen((v) => !v)}
                        listOpen={listOpen}
                        disabled={status !== "ready"}
                    />
                )}

                {/* Floating, draggable figure list — the patient's OWN figures. */}
                {(!view.immersive || listOpen) && (
                    <DraggablePanel title={t("constellation.list.title")} initialX={16} initialY={16} widthClassName="w-80">
                        <PuppetList
                            puppets={ownFigures}
                            selectedId={selectedId}
                            maxNameLength={data?.max_name_length}
                            onSelect={setSelectedId}
                            onRename={renamePuppet}
                            onRotate={rotatePuppet}
                            onRemove={removePuppet}
                            onEdit={openEdit}
                        />
                    </DraggablePanel>
                )}
              </div>
            </main>
            </div>

            {/* Add-figure picker — opens the figure grid directly from the
                immersive "+" button. */}
            {figurePickerOpen && (
                <PuppetBuilder
                    startOpen
                    hideTrigger
                    onClose={() => setFigurePickerOpen(false)}
                    onAdd={addPuppet}
                    disabled={status !== "ready"}
                    maxNameLength={data?.max_name_length}
                    therapistFigures={therapistFigures}
                    portalContainer={view.containerRef.current}
                />
            )}

            {/* Mobile: board is the hero; controls live in a bottom sheet. */}
            <button
                type="button"
                onClick={() => setToolsOpen(true)}
                className="fixed bottom-5 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full bg-brand-600 px-5 py-3 text-sm font-semibold text-white shadow-lg md:hidden"
            >
                <Plus className="h-5 w-5" />
                {t("constellation.patient.addFigureCta", "Add figure")}
            </button>

            {toolsOpen && (
                <div className="fixed inset-0 z-50 flex flex-col justify-end md:hidden">
                    <div
                        className="absolute inset-0 bg-black/30"
                        onClick={() => setToolsOpen(false)}
                        aria-hidden
                    />
                    <div
                        className="relative max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
                        role="dialog"
                        aria-label={t("constellation.patient.title")}
                    >
                        <div className="sticky top-0 flex items-center justify-end border-b bg-white px-2 py-2">
                            <button
                                type="button"
                                onClick={() => setToolsOpen(false)}
                                aria-label={t("common.close", "Close")}
                                className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        {controls}
                    </div>
                </div>
            )}

            {/* Edit menu — opened from a figure's ✎ button */}
            {editingPuppet && (
                <BoardOverlay container={view.containerRef.current}>
                <div
                    className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 sm:items-center"
                    onClick={() => setEditingId(null)}
                >
                    <div
                        className="w-full max-w-sm rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-base font-semibold">
                                {t("constellation.edit.title", { name: editingPuppet.name })}
                            </h2>
                            <button
                                type="button"
                                onClick={() => setEditingId(null)}
                                aria-label={t("common.close", "Close")}
                                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        {editingIsHuman && (
                            <div className="mb-4 flex flex-col gap-1 text-xs">
                                {t("constellation.builder.action")}
                                <div className="grid grid-cols-2 gap-1">
                                    {Object.values(FigureAction).map((act) => (
                                        <button
                                            key={act}
                                            type="button"
                                            onClick={() => setPuppetAction(editingPuppet.id, act)}
                                            aria-pressed={(editingPuppet.action ?? "stand") === act}
                                            className={`min-h-[36px] rounded-md border px-2 text-sm capitalize ${
                                                (editingPuppet.action ?? "stand") === act
                                                    ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200"
                                                    : "border-gray-100 hover:border-brand-200"
                                            }`}
                                        >
                                            {t(`constellation.action.${act}`)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {editingIsHuman && (
                            <div className="mb-4">
                                <FacePicker
                                    label={t("constellation.builder.editEmotion")}
                                    value={editingPuppet.face}
                                    onChange={(face) => setPuppetFace(editingPuppet.id, face)}
                                />
                            </div>
                        )}

                        <label className="flex flex-col gap-1 text-xs">
                            {t("constellation.builder.note")}
                            <input
                                type="text"
                                value={editingPuppet.note ?? ""}
                                maxLength={data?.max_name_length}
                                onChange={(e) => setPuppetNote(editingPuppet.id, e.target.value)}
                                placeholder={t("constellation.builder.notePlaceholder")}
                                className="rounded-md border px-2 py-1"
                            />
                        </label>
                    </div>
                </div>
                </BoardOverlay>
            )}

            {/* Note editor — opened when adding or editing a free-floating note */}
            {editingNote && (
                <BoardOverlay container={view.containerRef.current}>
                <div
                    className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 sm:items-center"
                    onClick={() => setEditingNoteId(null)}
                >
                    <div
                        className="w-full max-w-sm rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-base font-semibold">
                                {t("constellation.note.editTitle")}
                            </h2>
                            <button
                                type="button"
                                onClick={() => setEditingNoteId(null)}
                                aria-label={t("common.close", "Close")}
                                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <textarea
                            autoFocus
                            rows={4}
                            value={editingNote.text}
                            maxLength={data?.max_note_length}
                            onChange={(e) =>
                                dispatch({
                                    type: "editNote",
                                    id: editingNote.id,
                                    text: e.target.value,
                                })
                            }
                            placeholder={t("constellation.note.placeholder")}
                            aria-label={t("constellation.note.editTitle")}
                            className="w-full rounded-md border px-2 py-1 text-sm"
                        />
                        <div className="mt-3 flex justify-between">
                            <button
                                type="button"
                                onClick={() => removeNote(editingNote.id)}
                                className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                            >
                                {t("constellation.list.remove", "Delete")}
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditingNoteId(null)}
                                className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover"
                            >
                                {t("common.done", "Done")}
                            </button>
                        </div>
                    </div>
                </div>
                </BoardOverlay>
            )}

            {/* Zone editor — label, color, size for a floor delimitation area */}
            {editingZone && (
                <BoardOverlay container={view.containerRef.current}>
                <div
                    className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 sm:items-center"
                    onClick={() => setEditingZoneId(null)}
                >
                    <div
                        className="w-full max-w-sm rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="text-base font-semibold">
                                {t("constellation.zone.editTitle")}
                            </h2>
                            <button
                                type="button"
                                onClick={() => setEditingZoneId(null)}
                                aria-label={t("common.close", "Close")}
                                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <input
                            autoFocus
                            type="text"
                            value={editingZone.label}
                            maxLength={data?.max_zone_label}
                            onChange={(e) =>
                                dispatch({ type: "editZone", id: editingZone.id, label: e.target.value })
                            }
                            placeholder={t("constellation.zone.placeholder")}
                            aria-label={t("constellation.zone.editTitle")}
                            className="w-full rounded-md border px-2 py-1 text-sm"
                        />
                        <div className="mt-3 flex gap-2">
                            <button
                                type="button"
                                onClick={() =>
                                    dispatch({ type: "editZone", id: editingZone.id, shape: "circle" })
                                }
                                aria-pressed={editingZone.shape === "circle"}
                                className={`flex min-h-[36px] flex-1 items-center justify-center gap-1 rounded-md border px-2 text-sm ${
                                    editingZone.shape === "circle"
                                        ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200"
                                        : "border-gray-200 hover:border-brand-200"
                                }`}
                            >
                                <Circle className="h-4 w-4" /> {t("constellation.table.circle")}
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    dispatch({ type: "editZone", id: editingZone.id, shape: "square" })
                                }
                                aria-pressed={editingZone.shape === "square"}
                                className={`flex min-h-[36px] flex-1 items-center justify-center gap-1 rounded-md border px-2 text-sm ${
                                    editingZone.shape === "square"
                                        ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200"
                                        : "border-gray-200 hover:border-brand-200"
                                }`}
                            >
                                <Square className="h-4 w-4" /> {t("constellation.table.square")}
                            </button>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                            {ZONE_COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    aria-label={c}
                                    aria-pressed={editingZone.color === c}
                                    onClick={() =>
                                        dispatch({ type: "editZone", id: editingZone.id, color: c })
                                    }
                                    style={{ background: c }}
                                    className={`h-7 w-7 rounded-full border-2 ${
                                        editingZone.color === c ? "border-gray-800" : "border-white"
                                    }`}
                                />
                            ))}
                        </div>
                        <div className="mt-4 flex items-center gap-3">
                            <span className="text-xs text-gray-500">{t("constellation.zone.size")}</span>
                            <button
                                type="button"
                                onClick={() => resizeZoneStep(editingZone, -1)}
                                className="h-8 w-8 rounded-md border text-lg"
                            >
                                −
                            </button>
                            <button
                                type="button"
                                onClick={() => resizeZoneStep(editingZone, 1)}
                                className="h-8 w-8 rounded-md border text-lg"
                            >
                                +
                            </button>
                        </div>
                        <div className="mt-4 flex justify-between">
                            <button
                                type="button"
                                onClick={() => removeZone(editingZone.id)}
                                className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                            >
                                {t("constellation.list.remove", "Delete")}
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditingZoneId(null)}
                                className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover"
                            >
                                {t("common.done", "Done")}
                            </button>
                        </div>
                    </div>
                </div>
                </BoardOverlay>
            )}

            {/* Link editor — name, color, weight for a connection between figures */}
            {editingLink && (
                <LinkEditorModal
                    link={editingLink}
                    maxLabelLength={data?.max_link_label}
                    onChangeLabel={(label) =>
                        dispatch({ type: "editLink", id: editingLink.id, label })
                    }
                    onChangeColor={(color) =>
                        dispatch({ type: "editLink", id: editingLink.id, color })
                    }
                    onChangeWeight={(weight) =>
                        dispatch({ type: "editLink", id: editingLink.id, weight })
                    }
                    onRemove={() => links.removeLink(editingLink.id)}
                    onClose={links.closeLinkEditor}
                    portalContainer={view.containerRef.current}
                />
            )}
        </div>
    );
}
